/* eslint-disable */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, connect } from 'react-redux';
import { Filter, RefreshCcw, Loader2 } from 'lucide-react';
import { BackdropLoader } from '@/Components/Loaders';
import { Button } from '@/Components/ui/button';
import GlobalSearch from '@/Components/globalSearch/search';
import drawerActions from '@/Actions/drawer.actions';
import { filtersInfoChip } from '@/Helpers';
import TableWithTabs from '../AIFMSUMMARY/TableWithTabs';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import { useMasterData } from './hooks/useMasterData';
import { useMasterTableData } from './hooks/useMasterTableData';
import { useRecipeData } from './hooks/useRecipeData';
import moment from 'moment';

const exportInfo = {
	showExportBtn: true,
};

const LoadingSpinner = (isLoading) => (
	<BackdropLoader isLoading={isLoading} showProgress={isLoading} />
);

// Parent tabs for Master Data Summary
const masterTabs = [
	{ id: 'recipe', value: 'recipe', header: 'Recipe', label: 'Recipe' },
	// { id: 'product', value: 'product', header: 'Product', label: 'Product' },
	// { id: 'batch', value: 'batch', header: 'Batch', label: 'Batch' },
];

function MasterDataSummary({ stores }) {
	const dispatch = useDispatch();
	const [filters, setFilters] = useState({});
	const [search, setSearch] = useState({});
	const [activeTab, setActiveTab] = useState(masterTabs[0]);

	// Get the first BASE store as default
	const defaultStoreState = useMemo(() => {
		const firstBaseStore = stores?.find((store) => store?.type === 'BASE');
		if (firstBaseStore) {
			return {
				STORE: {
					id: firstBaseStore.id,
					name: firstBaseStore.name,
				},
			};
		}
		return {};
	}, [stores]);

	// Set default store in filters when defaultStoreState is available
	useEffect(() => {
		if (defaultStoreState?.STORE?.id) {
			setFilters((prevFilters) => ({
				...prevFilters,
				store: {
					id: defaultStoreState.STORE.id,
					name: defaultStoreState.STORE.name,
				},
			}));
		}
	}, [defaultStoreState]);

	// Use recipe data hook when recipe tab is active
	const {
		rawData: recipeRawData,
		isLoading: recipeIsLoading,
		fetchData: fetchRecipeData,
		refresh: refreshRecipe,
	} = useRecipeData({
		storeId: filters?.store?.id,
	});

	// Use master data hook for other tabs
	const {
		rawData: masterRawData,
		isLoading: masterIsLoading,
		fetchData: fetchMasterData,
		cacheTime,
		refresh: refreshMaster,
	} = useMasterData({
		subTab: activeTab?.value,
	});

	// Determine which data and loading state to use based on active tab
	const isRecipeTab = activeTab?.value === 'recipe';
	const rawData = isRecipeTab ? recipeRawData : masterRawData;
	const isLoading = isRecipeTab ? recipeIsLoading : masterIsLoading;

	const { getTableInfo, dropDownItems } = useMasterTableData(
		rawData,
		activeTab,
		search,
		isLoading
	);

	// Update filtersInfo to show store filter for all tabs and disable dateRange
	const filtersInfo = useMemo(() => {
		return {
			header: {
				store: false,
				date: true,
			},
			dateRange: {
				text: 'Date Range',
				show: false,
			},
			store: {
				text: 'Store',
				show: true,
			},
		};
	}, []);

	const handleTabChange = useCallback((tab) => {
		setActiveTab(tab);
		setSearch({});
	}, []);

	const handleDataFetch = useCallback((filter) => {
		const processedFilters = { ...filter };
		if (processedFilters?.dateRange) {
			processedFilters.dateRange.active = true;
		}
		setFilters(processedFilters);
	}, []);

	// Fetch data based on active tab
	useEffect(() => {
		if (isRecipeTab) {
			// For recipe tab, fetch when store is selected
			if (filters?.store?.id) {
				fetchRecipeData({ storeId: filters.store.id });
			}
		} else {
			// For other tabs, fetch when date range is selected
			if (filters?.dateRange) {
				fetchMasterData({ ...filters, subTab: activeTab?.value });
			}
		}
	}, [filters, activeTab?.value, isRecipeTab]);

	const exportData = () => {
		const headers = [];
		let csvData = [];
		rawData?.columns?.forEach((column) => {
			headers.push(column?.label);
		});
		const rows = rawData?.data?.map((item) => {
			const row = [];
			rawData?.columns?.forEach((header) => {
				const cellData = item?.[header?.key];
				const value =
					cellData &&
					typeof cellData === 'object' &&
					cellData.value !== undefined
						? cellData.value
						: cellData ?? '';
				row.push(value);
			});
			return row;
		});
		csvData = [headers, ...rows];

		const csvContent = csvData.map((row) => row.join(',')).join('\n');
		const csvFile = new Blob([csvContent], { type: 'text/csv' });
		const downloadLink = document.createElement('a');
		downloadLink.download = `MASTER_DATA_SUMMARY_${
			activeTab?.value
		}_${moment().format('YYYY-MM-DD')}.csv`;
		downloadLink.href = window.URL.createObjectURL(csvFile);
		downloadLink.style.display = 'none';
		document.body.appendChild(downloadLink);
		downloadLink.click();
		window.URL.revokeObjectURL(downloadLink.href);
		document.body.removeChild(downloadLink);
	};

	const [highlightCache, setHighlightCache] = useState(false);

	useEffect(() => {
		if (!isLoading && cacheTime && cacheTime !== 'Just now') {
			setHighlightCache(true);
			const timer = setTimeout(() => {
				setHighlightCache(false);
			}, 5000);
			return () => clearTimeout(timer);
		}
	}, [cacheTime, isLoading]);

	const FilterSection = useMemo(
		() => (
			<div className='flex flex-1 items-baseline gap-3'>
				<div className='flex-1 min-w-0'>
					<GlobalSearch
						placeholderText='Search'
						dropDownOptions={dropDownItems}
						searchDropDown={dropDownItems?.length > 0}
						setSelectedValue={setSearch}
					/>
				</div>
				<div className='flex-shrink-0'>
					{filtersInfoChip(filtersInfo, filters)}
				</div>
				<Button
					onClick={() =>
						dispatch(
							drawerActions.toggleFiltersDrawer(true, {
								handleDataExport: () => {
									exportData();
								},
							})
						)
					}
					className='flex justify-center gap-2 px-4 py-2 text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700 flex-shrink-0'
				>
					<span>Filter & Export</span>
					<Filter size={16} />
				</Button>
			</div>
		),
		[filters, dispatch, rawData, activeTab, dropDownItems, filtersInfo]
	);

	const ParentTabsSection = useMemo(
		() => (
			<div className='flex w-fit justify-center rounded-md bg-[#FDE6AD] border border-[#00000033]'>
				{masterTabs?.map((ele) => {
					const isActive = activeTab?.id === ele?.id;
					return (
						<div
							key={ele.id}
							className={`flex items-center gap-2 justify-center px-4 py-2 text-[12px] font-medium cursor-pointer whitespace-nowrap
              ${isActive ? 'bg-[#ffb703] rounded-md' : ''}`}
							onClick={() => handleTabChange(ele)}
						>
							{ele?.header}
						</div>
					);
				})}
			</div>
		),
		[activeTab, handleTabChange]
	);

	const getTabInfo = useCallback(() => {
		return {
			tabList: [],
			activeTabColor: 'bg-yellow-200',
			tabValue: '',
			handleTabChange: () => {},
		};
	}, []);

	return (
		<div className='p-2'>
			<style>
				{`
					@keyframes shake-tilt {
						0% { transform: rotate(0deg); }
						25% { transform: rotate(-5deg); }
						50% { transform: rotate(5deg); }
						75% { transform: rotate(-5deg); }
						100% { transform: rotate(0deg); }
					}
					.animate-shake-tilt {
						animation: shake-tilt 0.2s ease-in-out 1s 2;
					}
				`}
			</style>
			<ImagesFinder
				filtersInfo={filtersInfo}
				exportInfo={exportInfo}
				onSubmit={handleDataFetch}
				disableFutureDate
				hideDateRangeTimeType
				storeTypes='BASE'
				defaultState={defaultStoreState}
			/>

			<>
				<div className='flex flex-col gap-2'>
					<div className='flex justify-between items-center'>
						<h2 className='font-bold text-lg'>
							Master Data Summary
						</h2>
					</div>
					{FilterSection}
					<div className='flex flex-col gap-2 mb-2'>
						{ParentTabsSection}
					</div>
				</div>

				<TableWithTabs
					tableInfo={getTableInfo()}
					tabInfo={getTabInfo()}
					isLoading={isLoading}
					renderLegend={() => {}}
					height={250}
				/>
				{isLoading && <LoadingSpinner isLoading={isLoading} />}
			</>
		</div>
	);
}

const mapStateToProps = (state) => {
	const stores = state?.form?.STORES?.data?.LIST;
	return {
		stores,
	};
};

export default connect(mapStateToProps)(MasterDataSummary);
